import { useState } from "react";
import { useResponsive } from "@/utils/useResponsive";
import { Drawer, DrawerClose, DrawerContent } from "../ui/drawer";
import CloseIcon from '@/assets/svgs/close.svg?react';
import { Modal } from "../Modals/Modal.component";
import { DrawerContext } from "./DrawerContext";
import { useTranslation } from "react-i18next";
import Image from "../ui/Image/Image";

export type ServiceDrawerProps = {
  open: boolean;
  setOpen(open: boolean): void;
  content: React.ReactNode;
  onExit?: () => void;
}

export const ServiceDrawer = ({ open, setOpen, content, onExit }: ServiceDrawerProps) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [onClose, setOnClose] = useState(() => () => setOpen(false));
  const [onConfirmLeave, setOnConfirmLeave] = useState(() => () => { });
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <Drawer
      open={open}
      direction="right"
      handleOnly
      repositionInputs={false}
      onClose={onExit}
    >
      <DrawerContent className={`h-dvh lg:w-[45vw] xl:w-[35vw] max-w-full overflow-x-hidden fixed right-0 top-0 bottom-0 m-0 overflow-y-auto ${isMobile ? 'w-dvw' : ''}`}>
        <DrawerContext.Provider value={{ onClose, setOnClose, onConfirmLeave, setOnConfirmLeave, setIsModalOpen }}>
          <DrawerClose className="absolute right-4 top-6 rounded-xm ring-offset-background stroke-black transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <CloseIcon onClick={onClose} />
          </DrawerClose>
          {content}
        </DrawerContext.Provider>
      </DrawerContent>
      <Modal
        open={isModalOpen}
        title={t("modals.confirmation")}
        subtitle={t("modals.unsavedChanges")}
        primaryBtn={{
          label: t('buttons.continue'),
          onClick: () => {
            setIsModalOpen(false);
          }
        }}
        secondaryBtn={{
          label: t('buttons.leave'),
          onClick: () => {
            onConfirmLeave();
            setIsModalOpen(false);
            setOpen(false);
            setOnClose(() => () => setOpen(false));
            if (isMobile) {
              setTimeout(() => {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
              }, 300);
            }
          }
        }}
      />
    </Drawer>
  );
};

export type DrawerHeaderProps = {
  image: string;
  productName: string;
  productSku: string;
}

export const DrawerHeader = ({ image, productName, productSku }: DrawerHeaderProps) => {
  return (
    <div className="flex flex-row border-b pb-6 mb-6 pt-6">
      <div className="xl:pr-8">
        <Image
          width={120}
          height={120}
          className="rounded-md"
          src={image}
        />
      </div>
      <div className="flex-1">
        <div className="flex flex-col items-center justify-center xl:items-start h-full">
          <p className="text-textXLarge">{productName}</p>
          <p className="text-bodySmall opacity-50 sku">{productSku}</p>
        </div>
      </div>
    </div>
  )
}